import React, { useState } from 'react'
import { FileText } from 'lucide-react'
import { genId } from '../lib/id'
import { sanitizeInlineHtml } from '../lib/sanitizeHtml'
import { convertDocxToHtml } from '../integrations'
import FileDropzone from '../components/FileDropzone'
import type { WordCard } from '../types/LessonDoc'
import type { CardDef, CardEditorProps, CardPreviewProps } from './types'

// Word (.docx) → HTML qua mammoth (client-side). Giữ nguyên thành 1 thẻ.
const DOCX_ACCEPT = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
}

const Editor: React.FC<CardEditorProps> = ({ card, onChange }) => {
  const c = card as WordCard
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleFile = async (file: File) => {
    setBusy(true)
    setError(null)
    try {
      const { html } = await convertDocxToHtml(file)
      onChange({ html, sourceFileName: file.name })
    } catch (e) {
      setError((e as Error)?.message || 'Không đọc được file Word')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3">
      <FileDropzone
        accept={DOCX_ACCEPT}
        onFile={handleFile}
        busy={busy}
        label="Kéo-thả file .docx để chuyển thành nội dung"
        hint="Chuyển ngay trong trình duyệt, ảnh nhúng được giữ lại"
      />
      {c.sourceFileName && (
        <p className="text-xs text-emerald-600 font-medium">✓ Đã nhập: {c.sourceFileName}</p>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
      <label className="text-xs font-bold text-gray-500 uppercase">HTML nội dung (có thể chỉnh tay)</label>
      <textarea
        className="w-full min-h-[120px] p-3 border border-gray-200 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-blue-200"
        value={c.html}
        onChange={(e) => onChange({ html: e.target.value })}
      />
    </div>
  )
}

const Preview: React.FC<CardPreviewProps> = ({ card }) => {
  const c = card as WordCard
  if (!c.html) {
    return (
      <div className="p-4 rounded-lg bg-gray-50 border border-dashed border-gray-200 text-gray-400 text-sm text-center">
        Chưa có nội dung Word.
      </div>
    )
  }
  return (
    <div
      className="prose prose-sm max-w-none text-gray-800"
      dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(c.html) }}
    />
  )
}

export const wordCardDef: CardDef = {
  type: 'word',
  label: 'Word lý thuyết',
  description: 'File .docx chuyển thành 1 thẻ',
  icon: FileText,
  color: 'bg-emerald-50 text-emerald-600',
  createDefault: (): WordCard => ({ id: genId(), type: 'word', html: '' }),
  Editor,
  Preview,
}
